import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import UserAvatar from "@/components/app/UserAvatar";
import { Sparkles, AlertTriangle, ShieldAlert } from "lucide-react";

/**
 * "Recent moments" — equal-weight feed of the latest appreciations and
 * warnings across the team. Visible to anyone logged in.
 *
 * Appreciations sit on the left so good work is celebrated first;
 * warnings on the right keep things honest.
 */
export default function RecentMoments() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/conduct/recent?limit=5").then(({ data }) => {
      if (!cancelled) setData(data);
    }).catch(() => { if (!cancelled) setData({ warnings: [], appreciations: [] }); });
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  if ((data.appreciations?.length || 0) === 0 && (data.warnings?.length || 0) === 0) return null;

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recent-moments">
      <FeedColumn
        title="Latest appreciations"
        emptyText="No appreciations yet — be the first to recognise great work."
        icon={Sparkles}
        accent="emerald"
        items={data.appreciations || []}
        renderItem={(a) => (
          <Item
            key={a.id}
            recipient={a}
            reason={a.reason}
            badge={null}
            actor={a.issued_by_name}
            when={a.created_at}
            accent="emerald"
          />
        )}
      />
      <FeedColumn
        title="Latest warnings"
        emptyText="No active warnings — the team is on point."
        icon={ShieldAlert}
        accent="amber"
        items={data.warnings || []}
        renderItem={(w) => (
          <Item
            key={w.id}
            recipient={w}
            reason={w.reason}
            badge={w.severity === "hard"
              ? <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-red-100 text-red-800 rounded px-1.5 py-0.5"><ShieldAlert className="w-2.5 h-2.5" /> Hard</span>
              : <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-orange-100 text-orange-800 rounded px-1.5 py-0.5"><AlertTriangle className="w-2.5 h-2.5" /> Soft</span>}
            actor={w.issued_by_name}
            when={w.created_at}
            accent={w.severity === "hard" ? "red" : "orange"}
          />
        )}
      />
    </section>
  );
}

function FeedColumn({ title, emptyText, icon: Icon, accent, items, renderItem }) {
  const accentMap = {
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    red:     "text-red-700",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className={`flex items-center gap-2 ${accentMap[accent] || ""}`}>
        {Icon ? <Icon className="w-4 h-4" /> : null}
        <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-zinc-400 italic mt-3">{emptyText}</p>
      ) : (
        <ul className="mt-3 space-y-3">{items.map(renderItem)}</ul>
      )}
    </div>
  );
}

function Item({ recipient, reason, badge, actor, when, accent }) {
  const borderMap = {
    emerald: "border-l-emerald-400",
    orange:  "border-l-orange-400",
    red:     "border-l-red-400",
  };
  const ago = relativeTime(when);
  return (
    <li className={`flex items-start gap-3 pl-3 border-l-2 ${borderMap[accent] || "border-l-zinc-200"}`}>
      <UserAvatar user={{ name: recipient.user_name, avatar_path: recipient.user_avatar }} size={32} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">{recipient.user_name || "—"}</span>
          {badge}
          {ago && <span className="text-[10px] text-zinc-400">{ago}</span>}
        </div>
        {recipient.user_role && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {(recipient.user_role || "").replace(/_/g, " ")}{recipient.user_department ? ` · ${recipient.user_department}` : ""}
          </div>
        )}
        {reason && (
          <div className="text-[12px] text-zinc-700 mt-1 italic">"{reason}"</div>
        )}
        {actor && (
          <div className="text-[10px] text-zinc-400 mt-0.5">— {actor}</div>
        )}
      </div>
    </li>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
